import React, { memo, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import { useHistory } from '@core/router';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray, useFormConfig, useFormState, ValidationStatus } from '@core/utils/form';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { tap } from 'rxjs/operators';
import { FormText } from '@core/components/form/FormText';
import { useDidMount } from '@core/utils/hooks/react';
import { useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { FormInput, FormCheckbox } from '@core/components/form';
import { Button } from '@core/components/Button';
import { HTMLService } from '@business/services';
import { ProposalPreviewPageBloc } from '@modules/proposals/ViewProposalModal/ProposalPreviewPage/ProposalPreviewPageBloc';
import { SignerBlockSettings } from './SignerBlockSettings';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { R } from '@core/utils/r';
import { DOMNode } from 'html-react-parser';
import moment from 'moment';
import { of } from 'rxjs';
import { AppConfig } from '@core/utils/config';
import { FlashMessage } from '@core/components/FlashMessage';
import SignatureCanvas from 'react-signature-canvas';
import { buildDocumentUrl, documentIdFromHref } from '@business/services/proposal_service/signer_documents_service';
import { Formatter } from '@core/utils/formatter';

function defineSignerBlockItemForm(terms: number, variation?: SignerBlockVariation) {
  return formGroup({
    sign: formControl<string>({
      placeholder: 'Type here to create signature...',
      required: true,
    }),
    signaturePadData: formControl<string>({
      required: variation === SignerBlockVariation.signaturePad,
    }),
    email: formControl<string>({}),
    canSign: formControl<boolean>({}),
    name: formControl<string>({
      initialValue: 'John Doe',
    }),
    menuOrder: formControl<number>({
      initialValue: 0,
    }),
    terms: formGroup({
      ...R.fromPairs(R.times(terms).map(i => [`term-${i}`, formControl<boolean>({ initialValue: false, required: true })])),
    }),
  });
}

function defineSignerBlockForm(terms: number, variation?: SignerBlockVariation) {
  // Style settings (drawer Settings tab), the same as every other block. They start at the signing section's own
  // spacing (30px top and bottom, 20px sides from index.scss), so signers saved before this keep their look.
  const advance = defineBlockAdvanceForm();
  advance.patchValue({ paddingLeft: 20, paddingRight: 20 });

  return formGroup({
    items: formArray(() => defineSignerBlockItemForm(terms, variation)),
    advance,
  });
}
export type SignerWidgetItemFormData = FormDataType<typeof defineSignerBlockItemForm>;
export type SignerWidgetFormData = FormDataType<typeof defineSignerBlockForm>;
export type SignerWidgetForm = ReturnType<typeof defineSignerBlockForm>;

export enum SignerBlockVariation {
  Signer = 'Signer',
  signaturePad = 'signaturePad',
}

export const SignerBlock = memo((props: Proposal.BlockProps<SignerBlockVariation>) => {
  const { variation, preview, editable } = props;
  const [init, setInit] = useState(false);
  const [currentSignee, setCurrentSignee] = useState(0);
  const proposal = useProposalContext();
  const history = useHistory();
  const proposalData = useProposalDataContext();
  const config = AppConfig.get();
  const [isAccepted, setIsAccepted] = useState(true);
  const senders = useMemo(() => proposalData?.senders || [], [proposalData]);
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedSignature, setTypedSignature] = useState<string>('');
  const signee = useMemo(() => {
    if (proposalData?.signee) {
      return proposalData.signee;
    } else {
      return null;
    }
  }, [proposalData]);

  const mainProposalForm = useMemo(() => {
    if (proposal) return proposal.form;
  }, [proposal]);

  const subProjects = useObservable(proposal?.form.controls.subProjects.value$, []);

  const mainFormValues = useObservable(mainProposalForm && mainProposalForm.value$);
  const termsValue = useObservable(proposal?.form.controls.terms.value$, '');

  const termsOccurance = useMemo(() => R.occurrences(termsValue || '', '[]'), [termsValue]);

  const form = useMemo(() => defineSignerBlockForm(termsOccurance, variation), [termsOccurance, variation]);
  const itemValues = useObservable(form.controls.items.items$, []);
  const advanceValue = useObservable(form.controls.advance.value$);

  // Reset init when variation changes
  useEffect(() => {
    setInit(false);
  }, [variation]);

  useEffect(() => {
    if (signee && itemValues.length > 0 && !init) {
      setInit(true);
      itemValues.map((ctrl, index) => {
        const isRequired = ctrl.value.email == signee.email;
        // Set required based on variation
        if (variation === SignerBlockVariation.signaturePad) {
          ctrl.controls.signaturePadData.setConfig({ required: isRequired });
          ctrl.controls.sign.setConfig({ required: isRequired });
        } else {
          ctrl.controls.sign.setConfig({ required: isRequired });
        }
        setCurrentSignee(index);
        R.times(termsOccurance).map(i => {
          ctrl.controls.terms.controls[`term-${i}`].setConfig({ required: isRequired });
        });
      });
    }
  }, [init, itemValues, itemValues.length, signee, termsOccurance, variation]);

  const formValue = useNonNilObservable(form.value$);
  const [signature, setSignature] = useState(true);

  const previewPageBloc = proposal && (proposal.bloc as ProposalPreviewPageBloc);

  const onSubmit = useCallback(() => {
    if (previewPageBloc && signee) {
      // const selectedProducts = [];
      const selectedProducts = subProjects.filter(p => !!p.isChecked);
      const item = R.find(itemValues, i => i.value.email == signee.email);
      if (mainFormValues && item) {
        const signatureData = variation === SignerBlockVariation.signaturePad && item.value.sign ? item.value.sign : item.value.sign;
        return previewPageBloc
          .updateSender(mainFormValues, signee.id, { isSigned: 1, signedName: signatureData, email: item.value.email }, selectedProducts)
          .pipe(
            tap(() => setSignature(false)),
            tap(() => setCurrentSignee(0)),
            tap(data => {
              const senders = data.senders;
              if (senders && senders.every(s => s.is_signed) && !R.isEmpty(data.depositUrl)) {
                window.open(data.depositUrl, '_self');
              }
              //   return data.depositUrl && !R.isEmpty(data.depositUrl) && window.open(data.depositUrl, '_self');
            }),
          );
      }
    }
    return of(null);
  }, [itemValues, mainFormValues, previewPageBloc, signee, subProjects, variation]);

  useDidMount(() => {
    form.controls.items.push().patchValue({ menuOrder: 0 });
  });

  useConfigureBlock({ props, form, contentComponent: SignerBlockSettings, contentProps: { form, terms: proposal?.form.controls.terms } });
  const termsHtml = useMemo(() => {
    if (proposal) {
      const terms = proposal.form.value.terms;
      if (terms) {
        const arrayItems = terms.split('[]');
        let newString = '';
        arrayItems.forEach((item, index) => {
          newString =
            newString.concat(item) + (arrayItems.length === index + 1 ? '' : `<span class="proposal-checkbox" data-index="${index}">[]</span>`);
        });
        return newString;
      }
    }
    return '<span></span>';
  }, [proposal]);

  const documentUrl = useCallback(
    (documentId: string) => buildDocumentUrl({ appId: config?.appId, hash: proposalData?.documentLinks?.[documentId], documentId }),
    [config, proposalData],
  );

  const htmlOptions = useCallback(
    (domNode: DOMNode, email = null, currentSigneeIndex = 0, isDisabled = false) => {
      // If dom node it a then open it in new tab
      if (R.get(domNode, 'name') === 'a') {
        const rawHref = R.get(domNode, 'attribs.href');
        // A link to a Public Page / Agreement: point it at the copy frozen into this proposal, which
        // opens for the signed-out client and shows the values as they were when it was sent.
        const documentId = documentIdFromHref(rawHref);
        const href = documentId ? documentUrl(documentId) : rawHref;
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {R.get(domNode, 'children[0].data')}
          </a>
        );
      }
      if (R.get(domNode, 'attribs.class', '').includes('proposal-checkbox')) {
        const index = R.get(domNode, 'attribs.data-index');
        const hasControl =
          R.get(form.controls.items.items, currentSigneeIndex) &&
          R.get(form.controls.items.items[currentSigneeIndex].controls.terms.controls, `term-${index}`);
        if (!hasControl) {
          // No signee row to bind to (a template being built has no signees, and the form is rebuilt
          // empty when the [] count changes) — show a disabled box instead of the literal "[]".
          return <input type="checkbox" disabled className="mr-1" style={{ verticalAlign: 'middle' }} />;
        }
        if (
          R.get(form.controls.items.items, currentSigneeIndex) &&
          R.get(form.controls.items.items[currentSigneeIndex].controls.terms.controls, `term-${index}`)
        ) {
          const isChecked = !R.isEmpty(senders.find(s => s.email === email)?.signed_at);
          if (isChecked) {
            form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].setValue(true);
            form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].setConfig({ disabled: true });
          }
          if (isDisabled) {
            const value = form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`].value;
            console.log('value', value);
            return (
              <FormCheckbox
                noPadding
                control={form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`]}
                className="d-inline"
                disabled={true}
              />
            );
          }
          return (
            <FormCheckbox
              noPadding
              control={form.controls.items.items[currentSigneeIndex].controls.terms.controls[`term-${index}`]}
              className="d-inline"
              // disabled={props.preview}
            />
          );
        }
        return domNode;
      }
      return domNode;
    },
    [form.controls.items.items, senders],
  );

  useFormConfig(form, { onSubmit });
  const { submitting, status } = useFormState(form, { submitting: true, status: true });

  useEffect(() => {
    if (status == ValidationStatus.valid) {
      setIsAccepted(true);
    }
  }, [status]);

  const onPreSubmit = useCallback(() => {
    console.log('Form validation status:', status);
    console.log('Form is valid:', status === ValidationStatus.valid);

    // Debug signature pad validation specifically
    if (variation === SignerBlockVariation.signaturePad) {
      itemValues.forEach((ctrl, index) => {
        const signValue = ctrl.controls.sign.value;
        const signaturePadValue = ctrl.controls.signaturePadData.value;
        const signValid = ctrl.controls.sign.valid;
        const signaturePadValid = ctrl.controls.signaturePadData.valid;
        console.log(`Item ${index} - Sign value:`, signValue, 'Valid:', signValid);
        console.log(`Item ${index} - SignaturePad value:`, signaturePadValue, 'Valid:', signaturePadValid);
      });
    }

    setIsAccepted(status === ValidationStatus.valid);
  }, [status, variation, itemValues]);

  const pageCanSign = useMemo(() => {
    return history.location.pathname == '/proposal-preview';
  }, [history.location.pathname]);

  const clearSignaturePad = useCallback(() => {
    signaturePadRef.current?.clear();
  }, []);

  // Convert typed text to signature image
  const generateTypedSignature = useCallback((text: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = '48px Brush Script MT, cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    }
    return canvas.toDataURL();
  }, []);

  const autoSaveSignaturePad = useCallback(
    (itemIndex: number) => {
      let signatureData = '';
      if (signatureMode === 'draw') {
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
          signatureData = signaturePadRef.current.toDataURL();
        }
      } else if (signatureMode === 'type' && typedSignature.trim()) {
        signatureData = generateTypedSignature(typedSignature.trim());
      }

      if (signatureData) {
        const itemControl = itemValues[itemIndex];
        if (itemControl) {
          // Set both signature pad data and the main sign field
          itemControl.controls.signaturePadData.setValue(signatureData);
          itemControl.controls.sign.setValue(signatureData);
          // Mark as touched and trigger validation
          itemControl.controls.signaturePadData.markAsTouched();
          itemControl.controls.sign.markAsTouched();
          // Update validity
          itemControl.controls.signaturePadData.updateValueAndValidity();
          itemControl.controls.sign.updateValueAndValidity();
          // Force form validation update
          form.updateValueAndValidity();
        }
      }
    },
    [itemValues, form, signatureMode, typedSignature, generateTypedSignature],
  );

  const undoSignaturePad = useCallback(
    (itemIndex: number) => {
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      setTypedSignature('');
      // Clear the form values when undoing
      const itemControl = itemValues[itemIndex];
      if (itemControl) {
        itemControl.controls.signaturePadData.setValue('');
        itemControl.controls.sign.setValue('');
      }
    },
    [itemValues, documentUrl],
  );
  return (
    <BlockContainer
      advanceForm={advanceValue}
      className={classNames('block-signer', variation, preview, { 'has-text-color': !!advanceValue?.color })}
      isSection={props.isSection}
      sectionTitle={props.sectionTitle}
      fullWidth={false}
      editable={editable}
    >
      {/* <Container> */}
      <div style={{ display: 'flex' }}>
        {props.editable && (
          <div className="signer-centered" style={{ flex: 1 }}>
            {variation === SignerBlockVariation.signaturePad ? (
              <div className="signature-pad-preview">
                <div className="signature-pad-container">
                  <div className="signature-pad-header">
                    <span className="signature-label">Signature Pad:</span>
                  </div>
                  <div className="signature-pad-wrapper">
                    <div className="signature-canvas-placeholder">
                      <i className="fa fa-signature" style={{ fontSize: '48px' }}></i>
                      <p style={{ marginTop: '10px' }}>Signature pad will appear here for signers</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <FormInput control={formControl({ placeholder: 'John Smith' })} editable={false} disabled={true} />
            )}
            {HTMLService.toReact(
              !props.preview
                ? termsHtml
                : '<span>By signing I agree this is a legal representation of my signature for all purposes just the same as pen-and-paper signature.<br/></span>',
              htmlOptions,
            )}

          </div>
        )}

        {!props.editable &&
          itemValues.map((ctrl, i) => {
            const isSigneeRow = !!signee && signee.email == ctrl.controls.email.value && signee.can_sign && !signee.is_signed;
            // An expired proposal is read-only: no signing until it is resent
            const canSign = isSigneeRow && !proposalData?.isExpired && signature ? true : false;
            const hasSignaturePadData = variation === SignerBlockVariation.signaturePad && !R.isEmpty(ctrl.controls.sign.value);
            const showSignatureArea = !R.isEmpty(ctrl.controls.sign.value) || (canSign && signature) || hasSignaturePadData;
            const sender = senders.find(s => s.email === ctrl.controls.email.value);
            const showTerms = canSign && signature && pageCanSign;
            const isCurrentSignee = senders.find(s => s.email == ctrl.controls.email.value);
            return (
              <div className="signer-centered mr-2" key={i} style={{ flex: 1 }}>
                <FormText control={ctrl.controls.name} editable={editable} />

                <div className="mt-2">
                  {variation === SignerBlockVariation.signaturePad && (canSign || props.preview || hasSignaturePadData) ? (
                    <div className="signature-pad-container">
                      <div className="signature-pad-header">
                        {!hasSignaturePadData && <span className="signature-label">Please sign:</span>}
                        {canSign && (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {!hasSignaturePadData && (
                              <>
                                <Button
                                  extraClassNames={`btn-sm ${signatureMode === 'draw' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setSignatureMode('draw')}
                                  intent={signatureMode === 'draw' ? 'primary' : 'secondary'}
                                >
                                  Draw
                                </Button>
                                <Button
                                  extraClassNames={`btn-sm ${signatureMode === 'type' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setSignatureMode('type')}
                                  intent={signatureMode === 'type' ? 'primary' : 'secondary'}
                                >
                                  Type
                                </Button>
                              </>
                            )}
                            <Button
                              extraClassNames="btn-outline-secondary btn-sm signature-undo-btn"
                              onClick={() => undoSignaturePad(i)}
                              intent="secondary"
                            >
                              <i className="fa fa-undo"></i>
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="signature-pad-wrapper">
                        {hasSignaturePadData && !canSign ? (
                          <div className="signed-signature-display-main">
                            <img src={ctrl.controls.sign.value} alt="Signed signature" className="signature-preview-main" />
                          </div>
                        ) : props.preview && !canSign ? (
                          <div className="signature-canvas-placeholder">
                            <i className="fa fa-signature" style={{ fontSize: '48px' }}></i>
                            <p style={{ marginTop: '10px' }}>Signature pad</p>
                          </div>
                        ) : signatureMode === 'draw' ? (
                          <SignatureCanvas
                            ref={signaturePadRef}
                            canvasProps={{
                              width: 500,
                              height: 200,
                              className: 'signature-canvas',
                            }}
                            backgroundColor="#ffffff"
                            penColor="#000000"
                            velocityFilterWeight={0.7}
                            minWidth={0.5}
                            maxWidth={2.5}
                            onEnd={() => autoSaveSignaturePad(i)}
                          />
                        ) : (
                          <div style={{ padding: '20px' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Type your full name"
                              value={typedSignature}
                              onChange={e => setTypedSignature(e.target.value)}
                              onBlur={() => autoSaveSignaturePad(i)}
                              style={{
                                fontSize: '24px',
                                fontFamily: 'Brush Script MT, cursive',
                                padding: '20px',
                                textAlign: 'center',
                                border: 'none',
                                outline: 'none',
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {/* Show validation error for signature pad if required but not filled */}
                      {variation === SignerBlockVariation.signaturePad &&
                        canSign &&
                        ((ctrl.controls.sign.touched && ctrl.controls.sign.invalid) ||
                          (ctrl.controls.signaturePadData.touched && ctrl.controls.signaturePadData.invalid)) && (
                          <div className="text-danger mt-1 font-size-12">Signature is required</div>
                        )}
                    </div>
                  ) : (
                    <FormInput control={ctrl.controls.sign} editable={editable} disabled={!canSign} />
                  )}
                  {showTerms && (
                    <>
                      {HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value, i))}
                      {!isAccepted && termsOccurance > 0 && <div className="text-danger ">Please enter your name and accept all terms</div>}
                      <Button
                        loading={submitting}
                        extraClassNames="mt-2"
                        onClick={() => {
                          // if (config?.impersonate?.isLogin) {
                          //   return FlashMessage.error(
                          //     'Error',
                          //     'Actions such as accepting extras or change orders are disabled in Impersonate Client mode.',
                          //   );
                          // } else {
                            form.submit(() => {
                              setCurrentSignee(i);
                              onPreSubmit();
                            });
                          // }
                        }}
                        intent="primary"
                      >
                        Submit
                      </Button>
                    </>
                  )}

                  {isSigneeRow && pageCanSign && !!proposalData?.isExpired && (
                    <div className="text-danger font-size-12 mt-2">
                      <i className="bx bx-time-five mr-1" />
                      This proposal has expired
                      {proposalData.expiresAt ? ` (valid until ${Formatter.formatTime(proposalData.expiresAt)})` : ''}, so it can no longer be signed.
                    </div>
                  )}

                  {!pageCanSign && HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value, i, true))}

                  {/* {!showTerms &&
                      HTMLService.toReact(
                        !props.preview
                          ? termsHtml
                          : '<span>By signing I agree this is a legal representation of my signature for all purposes just the same as pen-and-paper signature.</span>',
                        e => htmlOptions(e, ctrl.controls.email.value),
                      )} */}

                  {!!sender && sender.is_signed == 1 && (
                    <>
                      {/* {!pageCanSign && HTMLService.toReact(termsHtml, e => htmlOptions(e, ctrl.controls.email.value))} */}
                      <span className="text-muted font-size-12">Signed on {moment(sender.signed_at).format('MM/DD/YYYY [at] YYYY hh:mm A')}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {/* </Container> */}
    </BlockContainer>
  );
});
